import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import SelectPlan from '../select-plan';
import type { IProps } from './modify-dialer-subscription-container';
import { BillingOption } from '../../../../../../shared/utils/billing-options';
import { ModifySubscriptionSteps, SetPlanDetails } from './types';
import RadioButtonTransfer from '../../../../../../shared/design-system/components/atoms/radio-button-transfer';
import {
  billingOptions,
  gePlanCycleBasedOnPlanType,
  getDisabledLabel,
  isBillingAndSubscriptionPageReady,
  isDialerValueBasedPlanVisible,
} from './utils/helper';
import {
  ModifyPlanAction,
  PlanType,
  SubscriptionPlanModes,
  SubscriptionPlans,
} from '../../../../../../shared/utils/subscription-plans';
import Spinner from '../../../../../../shared/design-system/components/atoms/custom-spinner';
import { getPlanMetaData } from '../select-plan/utils/get-plan-meta-data';
import {
  getPlanId,
  getPlanNameByPlanCycle,
  getPlanNameByPlanMode,
} from '../upgrade-plan/utils/helper';
import { getPlanCode } from '../select-plan/utils/helper';
import PaymentFailed from '../upgrade-plan/payment-failed';
import { executeOnRequestStatus } from '../../../../../../shared/utils/execute-on-request-status';
import { showGeneralErrorNotification } from '../../../../../../shared/utils/errors';
import DialerUpgradePlan from '../dialer-upgrade-plan';
import DialerPlans from '../dialer-plans';

const ModifyDialerSubscription: React.FC<IProps> = (containerProps) => {
  const location = useLocation<{
    modifySubscriptionStep: ModifySubscriptionSteps;
    isRedirectedFromBillingOptions: boolean;
    isRedirectedFromEmailAccounts?: boolean;
  }>();

  const {
    selectPlan,
    selectedPlanDetails,
    sendGetDialerSubscriptionResponse,
    sendGetDialerPlansResponse,
    sendGetPurchaseDialerModifySubscriptionRequestStatus,
    resetPurchaseDialerModifySubscriptionResponse,
    sendGetDialerSubscriptionRequestStatus,
    sendGetDialerPlansRequestStatus,
    subscriptionPlanStatus,
  } = containerProps;

  const [
    modifySubscriptionStep,
    setModifySubscriptionStep,
  ] = useState<ModifySubscriptionSteps>(ModifySubscriptionSteps.SelectPlan);
  const [planCycle, setPlanCycle] = useState<BillingOption>(
    gePlanCycleBasedOnPlanType(
      sendGetDialerSubscriptionResponse?.planCode,
      sendGetDialerSubscriptionResponse?.planMode,
    ),
  );
  const [isPaymentFailed, setIsPaymentFailed] = useState(false);

  const setPlanDetails = ({
    planCode,
    planType,
    planMode,
    planName,
    action,
  }: SetPlanDetails) => {
    const {
      planName: currentPlanName,
      planMode: currentPlanMode,
      planAmount,
      planId,
    } = sendGetDialerSubscriptionResponse || {};

    const getPlanName = getPlanNameByPlanMode(
      sendGetDialerPlansResponse,
      planCode,
      planMode,
    );

    let modifyAction = action;

    if (
      currentPlanMode === SubscriptionPlanModes.MonthlyMode &&
      planCycle === BillingOption.ANNUALLY
    ) {
      modifyAction = ModifyPlanAction.Update;
    } else if (currentPlanName === planName) {
      modifyAction = ModifyPlanAction.AddSlots;
    }

    const { displayName, price } =
      getPlanMetaData(planCode, planCycle, planName || getPlanName) || {};

    selectPlan({
      displayName: displayName || planName,
      planId:
        getPlanId(
          sendGetDialerPlansResponse,
          planName || getPlanName,
          planCode,
          planType,
        ) || planId,
      planPrice: price || Number(planAmount),
      planCode,
      planType,
      action: modifyAction,
    });
  };

  const handleModifySubscriptionStep = (step) =>
    setModifySubscriptionStep(step);

  const handelBillingOption = (value) => setPlanCycle(value);

  const handelSelectPlan = (
    planCode: SubscriptionPlans,
    planType: PlanType,
    planName,
    planMode: number,
    action,
  ) => {
    setPlanDetails({ planCode, planType, planMode, planName, action });
    handleModifySubscriptionStep(ModifySubscriptionSteps.SelectSlots);

  };

  const onPaymentFailed = () => {
    setIsPaymentFailed(true);
  };

  const onPaymentRetrieve = () => {
    setIsPaymentFailed(false);
    setModifySubscriptionStep(ModifySubscriptionSteps.SelectPlan);
  };

  useEffect(() => {
    const { modifySubscriptionStep: step } = location.state || {
      modifySubscriptionStep: null,
    };

    if (step) {
      setModifySubscriptionStep(step);
    }
  }, []);

  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetDialerSubscriptionRequestStatus,
      onPending: () => {
        setIsPaymentFailed(false);
      },
      onSuccess: () => {
        if (sendGetDialerSubscriptionResponse) {
          const {
            planCode,
            planName,
          } = sendGetDialerSubscriptionResponse;
          setPlanCycle(
            gePlanCycleBasedOnPlanType(
              sendGetDialerSubscriptionResponse?.planCode,
              sendGetDialerSubscriptionResponse?.planMode,
            ),
          );
          setPlanDetails({
            planCode,
            planName,
            action: ModifyPlanAction.AddSlots,
          });
        }
      },
    });
  }, [
    sendGetDialerSubscriptionRequestStatus,
    sendGetDialerPlansRequestStatus,
  ]);

  useEffect(() => {
    if (selectedPlanDetails && sendGetDialerSubscriptionResponse) {
      const { planName, planCode } = sendGetDialerSubscriptionResponse;
      setPlanDetails({
        planCode:
          planCode === SubscriptionPlans.PAID_MONTHLY ||
          planCode === SubscriptionPlans.PAID_YEARLY
            ? getPlanCode(planCycle)
            : planCode,
        planName: getPlanNameByPlanCycle(
          selectedPlanDetails.displayName || planName,
          planCycle,
        ),
        action: selectedPlanDetails.action,
      });
    }
  }, [planCycle]);

  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetPurchaseDialerModifySubscriptionRequestStatus,
      onFailed: () => {
        containerProps.hideLoading();
        onPaymentFailed();
        resetPurchaseDialerModifySubscriptionResponse();
      },
    });
  }, [sendGetPurchaseDialerModifySubscriptionRequestStatus]);

  useEffect(() => {
    const {
      sendGetDialerSubscriptionRequest,
      sendGetDialerPlansRequest,
    } = containerProps;
    sendGetDialerSubscriptionRequest();
    sendGetDialerPlansRequest();
  }, []);

  const {
    sendGetConnectedUsersAndEmailAccountsRequestError,
    sendGetConnectedUsersAndEmailAccountsRequestStatus,
    resetConnectedUsersAndEmailAccountsRequest,
  } = containerProps;

  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetConnectedUsersAndEmailAccountsRequestStatus,
      onSuccess: () => {
        resetConnectedUsersAndEmailAccountsRequest();
      },
      onFailed: () => {
        showGeneralErrorNotification(
          sendGetConnectedUsersAndEmailAccountsRequestError?.message,
        );
        resetConnectedUsersAndEmailAccountsRequest();
      },
    });
  }, [sendGetConnectedUsersAndEmailAccountsRequestStatus]);

  if (isPaymentFailed) {
    return <PaymentFailed onRetryPayment={() => onPaymentRetrieve()} />;
  }

  const isRequestPending = isBillingAndSubscriptionPageReady({
    sendGetDialerSubscriptionRequestStatus,
    sendGetDialerPlansRequestStatus,
    sendGetDialerSubscriptionResponse,
    selectedPlanDetails,
  });

  const subscriptionStep = () =>
    modifySubscriptionStep === ModifySubscriptionSteps.SelectPlan ? (
      <div className="d-flex justify-content-center mt-4">
        <SelectPlan
          planCycle={planCycle}
          onSelectPlan={(planCode, planType, planName, planMode, action) =>
            handelSelectPlan(planCode, planType, planName, planMode, action)
          }
          sendGetAccountSubscriptionResponse={sendGetDialerSubscriptionResponse}
          sendGetPlansResponse={sendGetDialerPlansResponse}
        />
      </div>
    ) : (
      <DialerUpgradePlan
        containerProps={containerProps}
        planCycle={planCycle}
        isRedirectedFromEmailAccounts={
          location.state?.isRedirectedFromEmailAccounts
        }
      />
    );

  if (isRequestPending) {
    return <Spinner />;
  }

  if (
    isDialerValueBasedPlanVisible(
      sendGetDialerSubscriptionResponse?.planCode,
      sendGetDialerSubscriptionResponse?.planType,
      subscriptionPlanStatus,
    )
  ) {
    return <DialerPlans />;
  }

  return (
    <>
      <div className="d-flex flex-column align-items-center">
        {planCycle !== BillingOption.LIFETIME && (
          <div className="d-flex flex-column align-items-center mt-4">
            {/* <div className="modify-subscription__highlight-container d-flex justify-content-center align-items-center position-relative font-italic">
              <span className="semibold-1 font-8 orange-txt-16">
                {t(`messages.${getHighlightMessage(planCycle)}`)}
              </span>
              <div className="modify-subscription__circle">
                <ImageIcon src={Images.HalfCircle} />
              </div>
            </div> */}
            <RadioButtonTransfer
              options={billingOptions}
              onChange={handelBillingOption}
              value={planCycle}
              disabledLabel={getDisabledLabel(sendGetDialerSubscriptionResponse)}
            />
          </div>
        )}
      </div>

      {subscriptionStep()}
    </>
  );
};

export default ModifyDialerSubscription;
